import type {
  Options,
  SeriesColumnOptions,
  XAxisOptions,
  YAxisOptions,
} from 'highcharts';
import { stringify } from 'query-string';
import type { ExportResponse } from 'api/async-export';
import type { Export } from 'api/reports/csat';
import type { ColumnOrder } from 'components/organisms/grid-custom-column';
import { QueryCombinerType } from 'components/organisms/tasks/combiner';
import { SourceType } from 'components/organisms/tasks/fields';
import { Operation } from 'components/organisms/tasks/filters/operations';
import type { GroupedFilter } from 'components/organisms/tasks/grouped-filter-popover';
import {
  PreviewWidgetPayload as PreviewWidgetPayloadCustom,
  WidgetPayload,
  WidgetResponse as WidgetResponseCustom,
} from 'components/pages/custom-reports/types';
import { HttpClient } from '../http-client';
import type { Timestamp } from '../models';
import type { Project } from '../project';

export enum WidgetType {
  CustomCard = 'CUSTOM_CARD',
  CustomChart = 'CUSTOM_CHART',
  /**
   * @deprecated Not in use
   */
  InferredProgressByProjectOwner = 'INFERRED_PROGRESS_BY_PROJECT_OWNER',
  /**
   * @deprecated Not in use
   */
  ProjectByInferredProgress = 'PROJECT_BY_INFERRED_PROGRESS',
  /**
   * @deprecated Not in use
   */
  InferredProgressByTemplate = 'INFERRED_PROGRESS_BY_TEMPLATE',
  TaskByStatus = 'TASK_BY_STATUS',
  StatusByPhase = 'STATUS_BY_PHASE',
  ProjectByStatus = 'PROJECT_BY_STATUS',
  ProjectCount = 'PROJECT_COUNT',
  ARR = 'TOTAL_ARR',
}

export const NumberWidgetTypes = [
  WidgetType.ARR,
  WidgetType.ProjectCount,
  WidgetType.CustomCard,
];

export enum WidgetDefaultNumberFields {
  ProjectCount = 'project_count',
  CustomerCount = 'customer_count',
  CSAT = 'csat',
  BudgetedHours = 'budgeted_hours',
  ProjectHours = 'project_hours',
  RemainingMinutes = 'remaining_minutes',
}

export enum WidgetNativeNumberFields {
  ARR = 'annualizedRecurringRevenue',
  ProjectFee = 'projectFee',
}

export enum WidgetNativeValues {
  ARR = 'arr',
  ProjectFee = 'project_fee',
}

export enum FieldClass {
  NativeField = 'NATIVE',
  CustomField = 'CUSTOM',
  DefaultField = 'DEFAULT',
}

export enum UnitPosition {
  Prefix = 'PREFIX',
  Suffix = 'SUFFIX',
}

export enum AggregateType {
  Total = 'SUM',
  Average = 'AVG',
  Count = 'COUNT',
  None = 'NONE',
}

export enum VerticalAxisDefaultTaskFields {
  TaskCount = 'count',
  TaskCSAT = 'csat',
  TaskTrackedMinutes = 'trackedMinutes',
  TaskEffort = 'effort',
  TaskProgress = 'progress',
}

export enum VerticalAxisDefaultProjectFields {
  ProjectCount = 'count',
  CSAT = 'csat',
  ProjectBugetedHours = 'budgetedHours',
  ProjectTrackedMinutes = 'trackedMinutes',
  ProjectAge = 'projectAge',
  ProjectRemainingMinutes = 'remainingMinutes',
}

export enum HorizontalAxisDefaultTaskFields {
  TaskStartDate = 'startDate',
  TaskDueDate = 'dueDate',
  TaskActualStartDate = 'actualStartDate',
  TaskActualDueDate = 'actualDueDate',
  TaskStatus = 'status',
  TaskAssignee = 'taskAssignee',
}

export enum HorizontalAxisDefaultProjectFields {
  ProjectStatus = 'status',
  Templates = 'template',
  ProjectCreatedDate = 'createdDate',
  ProjectStartDate = 'startDate',
  ProjectDueDate = 'dueDate',
  ActualProjectStartDate = 'actualStartDate',
  ActualProjectCompletionDate = 'actualDueDate',
  ProjectOwner = 'projectOwner',
}

export const NativeDateFields = [
  HorizontalAxisDefaultProjectFields.ProjectCreatedDate,
  HorizontalAxisDefaultProjectFields.ProjectStartDate,
  HorizontalAxisDefaultProjectFields.ProjectDueDate,
  HorizontalAxisDefaultProjectFields.ActualProjectStartDate,
  HorizontalAxisDefaultProjectFields.ActualProjectCompletionDate,
  HorizontalAxisDefaultTaskFields.TaskDueDate,
  HorizontalAxisDefaultTaskFields.TaskStartDate,
  HorizontalAxisDefaultTaskFields.TaskActualDueDate,
  HorizontalAxisDefaultTaskFields.TaskActualStartDate,
];

export const excludedTaskFieldsForCustomChart = ['priority'];

export enum WidgetSourceType {
  Company = 'COMPANY',
}

export enum GroupByDefaultProjectFields {
  ProjectOwner = 'projectOwner',
  ProjectStatus = 'status',
}

export enum GroupByDefaultTaskFields {
  TaskStatus = 'status',
}

export enum ZoomLevel {
  Weekly = 'WEEK',
  Monthly = 'MONTH',
  Yearly = 'YEAR',
}

export interface BaseFieldFilter {
  operation: Operation;
  value?: string;
  sourceType: SourceType;
}

export interface NativeFieldFilter extends BaseFieldFilter {
  name: string;
  key?: string;
}

export interface CustomFieldFilter extends BaseFieldFilter {
  fieldId: number;
  key?: string;
}

export type SimpleSearchFieldFilter = NativeFieldFilter | CustomFieldFilter;

export interface SimpleFilter {
  nativeFields?: NativeFieldFilter[];
  customFields?: CustomFieldFilter[];
}

export interface SimpleSearchFilter extends SimpleFilter {
  match: QueryCombinerType;
}

export interface ChartFilter {
  projectsFilter: GroupedFilter;
  tasksFilter: GroupedFilter;
}

export enum CustomCardDateType {
  Summary = 'SUMMARY',
  Number = 'NUMBER',
  Date = 'DATE',
  User = 'USER',
  Value = 'VALUE',
}

export interface CustomField {
  fieldId: number;
  fieldClass: FieldClass;
  fieldSource?: string;
  aggType?: string;
}

export interface NativeAndDefaultField {
  name: string;
  fieldClass: FieldClass;
  fieldSource?: string;
  aggType?: string;
}

export type Field = NativeAndDefaultField | CustomField;

export interface FieldOption {
  name?: number;
  opposite?: boolean;
  label: string;
  value: Field;
}
interface CardMetaFields {
  unitName?: string;
  unitType: UnitPosition;
  aggType: AggregateType;
  field: Field;
  customCardDateType?: CustomCardDateType;
}

export interface ChartMetaFields {
  chart: {
    chartType: string;
  };
  aggType: AggregateType;
  xAxis: Axis;
  yAxis: Axis;
  groupBy?: Field;
  zoomLevel?: ZoomLevel;
  field?: Field;
}

export interface EditDefaultWidgetPayload {
  widgetName: string;
  simpleSearchFilter: SimpleSearchFilter;
}

export interface EditCustomCardWidgetPayload extends EditDefaultWidgetPayload {
  metaData: CardMetaFields;
}

interface EditCustomChartWidgetPayload extends EditDefaultWidgetPayload {
  widgetObjectType: string;
  metaData: ChartMetaFields;
}

interface CreateDefaultWidgetPayload extends EditDefaultWidgetPayload {
  dashboardId: number;
  widgetType: WidgetType;
  widgetOrder?: number;
}

interface DefaultWidget extends CreateDefaultWidgetPayload {
  widgetId: number;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  enabled: boolean;
  deleted: boolean;
  widgetOrder: number;
}

interface PreviewCardWidgetPayload {
  simpleSearchFilter: SimpleSearchFilter;
  widgetType: WidgetType;
  metaData: {
    aggType: string;
    field: Field;
  };
}

export interface Axis {
  title: string;
  field: Field;
}
interface PreviewChartWidgetPayload {
  simpleSearchFilter: SimpleSearchFilter;
  widgetType: WidgetType;
  widgetObjectType: string;
  metaData: ChartMetaFields;
}

export interface CreateCustomCardWidgetPayload
  extends CreateDefaultWidgetPayload {
  metaData: CardMetaFields;
}
export interface CreateCompanyCustomCardWidgetPayload
  extends CreateCustomCardWidgetPayload {
  widgetObjectType: string;
}

export interface CreateCustomChartWidgetPayload
  extends CreateDefaultWidgetPayload {
  widgetObjectType: string;
  metaData: ChartMetaFields;
}

export interface ColumnMeta {
  columnOrder: ColumnOrder[];
  lastUpdatedBy: number;
  lastUpdatedAt?: number;
}

export interface EditColumnOrderPayload {
  columnMeta: ColumnMeta;
}

export type PreviewWidgetPayload =
  | PreviewCardWidgetPayload
  | PreviewChartWidgetPayload;

export type CreateWidgetPayload =
  | CreateDefaultWidgetPayload
  | CreateCustomCardWidgetPayload
  | CreateCustomChartWidgetPayload
  | CreateCompanyCustomCardWidgetPayload;

export type EditWidgetPayload =
  | EditDefaultWidgetPayload
  | EditCustomCardWidgetPayload
  | EditCustomChartWidgetPayload
  | EditColumnOrderPayload;

export type CustomCardWidget = CreateCustomCardWidgetPayload & DefaultWidget;
export type CustomChartWidget = CreateCustomChartWidgetPayload & DefaultWidget;

export type Widget = DefaultWidget | CustomCardWidget | CustomChartWidget;

export interface Xaxis extends XAxisOptions {
  categories: string[];
  categoryDrillDowns?: SimpleFilter[];
  categoryIds?: number[];
  field?: Field;
}

interface Yaxis extends YAxisOptions {
  field?: Field;
}

export interface ChartOptions extends Options {
  series?: SeriesColumnOptions[];
  xAxis?: Xaxis;
  yAxis: Yaxis;
  groupBy?: Field;
}

export interface WidgetResponse {
  dasboardId: number;
  widgetId: number;
  widgetType: WidgetType;
  response: ChartOptions;
}
export interface WidgetProps {
  widget: Widget;
  isDragging?: boolean;
}

export interface WidgetOrder {
  widgetId: number;
  widgetOrder: number;
}

export interface DrillDownPayload {
  drillDownFilter?: SimpleFilter;
  dashboardFilter?: SimpleSearchFilter;
  columnOrder?: string[];
}

export enum DrillDownExportType {
  PROJECT = 'PROJECT',
  TASK = 'TASK',
}

export type DrillDownExportResponse =
  | {
      exportMetaData: Export;
      drillDownExportType: DrillDownExportType.PROJECT;
    }
  | {
      asyncProcessResponse: ExportResponse;
      drillDownExportType: DrillDownExportType.TASK;
    };

const RESOURCE_URL = '/widgets';

class WidgetHttpClient extends HttpClient {
  public create = (payload: CreateWidgetPayload): Promise<Widget> => {
    return this.post(RESOURCE_URL, payload);
  };

  public editWidget = (
    widgetId: number,
    payload: EditWidgetPayload,
  ): Promise<Widget> => {
    return this.put(`${RESOURCE_URL}/${widgetId}`, payload);
  };

  public previewWidget = (payload: PreviewWidgetPayload): Promise<Options> => {
    return this.post(`${RESOURCE_URL}/preview-widget`, payload);
  };

  public previewWidgetV1 = (
    payload: PreviewWidgetPayloadCustom,
  ): Promise<Options> => {
    return this.post(`${RESOURCE_URL}/preview-widget-v1`, payload);
  };

  public getWidget = <WidgetType>(widgetId: number): Promise<WidgetType> => {
    return this.get(`${RESOURCE_URL}/${widgetId}`);
  };

  public resolveMultipleWidgets = (
    dashboardId: number,
    widgetIds: number[] = [],
    simpleSearchFilter: SimpleSearchFilter | null = null,
  ): Promise<WidgetResponse[]> => {
    return this.post(`${RESOURCE_URL}/resolve-widgets`, {
      dashboardId: Number(dashboardId),
      ...(simpleSearchFilter ? { simpleSearchFilter } : {}),
      ...(widgetIds.length ? { widgetIds } : {}),
    });
  };

  public resolveMultipleWidgetsV1 = (
    payload: WidgetPayload,
  ): Promise<WidgetResponseCustom[]> => {
    return this.post(`${RESOURCE_URL}/resolve-widgets-v1`, {
      simpleSearchFilter: payload.simpleSearchFilter,
      timePeriodFilter: payload.timeRangeFilter,
      sortOrder: payload.sortOrder,
      widgetIds: payload.widgetIds,
    });
  };

  public updateWidgetOrder = (
    dashboardId: number,
    widgetOrderDetailsList: WidgetOrder[],
  ): Promise<void> =>
    this.put(`${RESOURCE_URL}/update-order`, {
      dashboardId,
      widgetOrderDetailsList,
    });

  public updateWidgetOrderBySourceId = (
    sourceType: WidgetSourceType,
    widgetOrderDetailsList: WidgetOrder[],
  ): Promise<void> =>
    this.put(`${RESOURCE_URL}/update-order`, {
      entitySource: {
        sourceType,
      },
      widgetOrderDetailsList,
    });

  public deleteWidget = (widgetId: number): Promise<void> => {
    return this.delete(`${RESOURCE_URL}/${widgetId}`);
  };

  public resolveWidget = (widgetId: number): Promise<ChartOptions> => {
    return this.get(`${RESOURCE_URL}/${widgetId}/resolve-widget`);
  };

  public drillWidget = (widgetId: number, payload: any): Promise<Project[]> => {
    return this.post(
      `${RESOURCE_URL}/${widgetId}/drill-widget?lite=true`,
      payload,
    );
  };

  public drillWidgetV2 = (
    widgetId: number,
    payload: DrillDownPayload,
  ): Promise<Project[]> => {
    return this.post(
      `${RESOURCE_URL}/${widgetId}/drill-down-items?lite=true`,
      payload,
    );
  };

  public drillDownExport = (
    widgetId: number,
    payload: DrillDownPayload,
  ): Promise<DrillDownExportResponse> => {
    return this.post(
      `${RESOURCE_URL}/${widgetId}/drill-down-items/export`,
      payload,
    );
  };

  public getDrillDownColumnOrder = (widgetId: number): Promise<ColumnMeta> => {
    return this.get(`${RESOURCE_URL}/${widgetId}/drill-down-column-order`);
  };

  public getWidgetsBySource = <WidgetType>(
    sourceType: WidgetSourceType,
  ): Promise<WidgetType[]> => {
    return this.get<WidgetType[]>(
      `${RESOURCE_URL}?${stringify({ sourceType })}`,
    );
  };
}

export const WidgetAPI = new WidgetHttpClient();
